import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { authAction, disclaimerAction } from "@redux/modules";
import showErrMessage from "@components/ShowErrMessage";
import showMessage from "@components/ShowMessage";

import "./styles.scss";

/**
 * Sorumluluk Reddi Beyanı modal sayfası.
 *
 * Akış:
 *  - PrivateRoute, login sonrası disclaimer durumunu çekip Redux'a yazar.
 *  - Kullanıcının onaylaması GEREKİYORSA (required=true) PrivateRoute, hangi URL'e
 *    gitmeye çalışırsa çalışsın bu sayfaya yönlendirir. URL bar manuel değişikliği
 *    de bu sayede engellenir.
 *  - Bu sayfa store'dan disclaimer'ı okur; gerek YOKSA orijinal hedefe redirect eder.
 *  - "Kabul Ediyorum" → backend'e DisclaimerCheck kaydı → Redux required=false →
 *    PrivateRoute artık serbest bırakır → consumeRedirectTarget()'a navigate.
 *  - "Reddediyorum" → backend logout → /login.
 *  - Modal kapatılamaz: ESC tuşu engellenir, X butonu yoktur.
 */
const Disclaimer = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const isAuthenticated = useSelector((store) => store.auth.isAuthenticated);
  const { disclaimer, isLoading, isSaving, initialized, required } =
    useSelector((store) => store.disclaimer);

  const [submitting, setSubmitting] = useState(false);

  /**
   * PrivateRoute, kullanıcının asıl gitmek istediği sayfayı sessionStorage'a yazar.
   * Login.js de aynı mekanizmayı kullanır. Onay sonrası bu hedefe yönlendiririz.
   */
  const consumeRedirectTarget = () => {
    const target = sessionStorage.getItem("postDisclaimerRedirect") || "/home";
    sessionStorage.removeItem("postDisclaimerRedirect");
    return target;
  };

  // Authentication korumalı sayfa - giriş yapılmamışsa login'e gönder
  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/login", { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Disclaimer durumu çekilip required=false ise (artık göstermeye gerek yok)
  // doğrudan orijinal hedefe yönlendir. Fetch'i PrivateRoute yapıyor.
  useEffect(() => {
    if (initialized && required === false) {
      navigate(consumeRedirectTarget(), { replace: true });
    }
  }, [initialized, required, navigate]);

  const handleAccept = async () => {
    if (!disclaimer?.id || submitting) return;
    setSubmitting(true);
    try {
      await dispatch(disclaimerAction.saveDisclaimerCheck(disclaimer.id));
      showMessage("Onayınız kaydedildi.");
      // SAVE_DISCLAIMER_CHECK_FULFILLED reducer'da required=false yapacak,
      // yukarıdaki useEffect (initialized=true && required=false şartıyla)
      // sessionStorage'daki orijinal hedefe tek seferlik yönlendirmeyi yapacak.
      // Burada açık navigate ETMİYORUZ — aksi halde double-navigate olup
      // sessionStorage tüketildikten sonra useEffect /home varsayılanına düşer.
    } catch (error) {
      showErrMessage(
        error?.message || "Onay kaydedilirken bir hata oluştu."
      );
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      // ÖNEMLİ: tracking update'ini logout'TAN ÖNCE yap. Logout sonrası JWT
      // geçersiz olduğu için /secure/login/disclaimer/status çağrısı 401 döner.
      await dispatch(disclaimerAction.markDisclaimerRejected());

      const accessToken = localStorage.getItem("token");
      const refreshToken = localStorage.getItem("refreshToken");
      await dispatch(authAction.logout(accessToken, refreshToken));
    } catch (e) {
      // Logout backend'den hata dönse bile lokal state'i temizleyip login'e gönder
    } finally {
      sessionStorage.removeItem("postDisclaimerRedirect");
      dispatch(disclaimerAction.resetDisclaimer());
      navigate("/login", { replace: true });
    }
  };

  // Henüz veri yoksa boş ekran (PrivateRoute fetch'i devam ediyor)
  if (!initialized || !disclaimer || !disclaimer.id) {
    return null;
  }

  return (
    <div
      className="disclaimer-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="disclaimerTitle"
      onKeyDown={(e) => {
        // ESC ile kapanmayı engelle
        if (e.key === "Escape") {
          e.preventDefault();
          e.stopPropagation();
        }
      }}
    >
      <div className="disclaimer-modal">
        <div className="disclaimer-header">
          <h4 id="disclaimerTitle" className="mb-0">
            {disclaimer.name || "Sorumluluk Reddi Beyanı"}
          </h4>
        </div>
        <div className="disclaimer-body">
          {isLoading ? (
            <div className="text-center py-5">
              <span className="spinner-border" role="status" />
            </div>
          ) : (
            <div
              className="disclaimer-content"
              // Disclaimer metni admin TinyMCE'den geliyor (HTML)
              dangerouslySetInnerHTML={{ __html: disclaimer.description || "" }}
            />
          )}
        </div>
        <div className="disclaimer-footer">
          <button
            type="button"
            className="btn btn-outline-danger px-4"
            onClick={handleReject}
            disabled={submitting || isSaving}
          >
            Reddediyorum
          </button>
          <button
            type="button"
            className="btn btn-blue px-4"
            onClick={handleAccept}
            disabled={submitting || isSaving}
          >
            {(submitting || isSaving) && (
              <span className="spinner-border spinner-border-sm me-2" />
            )}
            Şart ve Koşulları Okudum, Anladım ve Kabul Ediyorum
          </button>
        </div>
      </div>
    </div>
  );
};

export default Disclaimer;
